import AppFormContainer from '@nxsys/components/AppFormContainer';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { showMessage } from '@nxsys/utils/message';
import { Option } from '@nxsys/types/models/Common';
import { getAppConfig } from '@nxsys/utils';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { some } from 'lodash';
import { Ref, useImperativeHandle } from 'react';
import { TieredFee } from '../../invoices/components/TieredFeeTableModal';
import CommunicationSection from './payroll-finance-sections/CommunicationSection';
import ExpenseSection from './payroll-finance-sections/ExpenseSection';
import HolidayPaySection from './payroll-finance-sections/HolidayPaySection';
import InvoicingBasicSection from './payroll-finance-sections/InvoicingBasicSection';
import MarginSection from './payroll-finance-sections/MarginSection';
import PaymentDetailsSection from './payroll-finance-sections/PaymentDetailsSection';
import PaymentModelSection from './payroll-finance-sections/PaymentModelSection';
import PayslipSettingSection from './payroll-finance-sections/PayslipSettingSection';
import PayslipTemplatesSection from './payroll-finance-sections/PayslipTemplatesSection';
import RestrictedHoursSection from './payroll-finance-sections/RestrictedHoursSection';
import TrackingSection from './payroll-finance-sections/TrackingSection';

export type PayrollFinanceFormValues = {
  invoicing: {
    vatRegNo: string;
    vatRate: Option<string> | null;
    domesticReverseChargeApplicable: boolean | null;
    creditLimit: string;
    creditTerm: string;
    paymentTerm: Option<string> | null;
    invoicingMethod: Option<string> | null;
    invoicingEmail: string;
    ccEmail: string;
  };
  invoiceSetting: {
    raiseInvoice: boolean | null;
    raiseInvoicePEO: boolean | null;
    invoiceRaisedAfter: Option<string> | null;
    invoiceRaisedMethod: Option<string> | null;
    dueDate: string;
    rule: Option<string> | null;
    invoiceForSalary: boolean | null;
    vatSalaryRate: number;
    vatSalaryRateCode: Option<string> | null;
    invoiceForSalaryDescription: string;
    invoiceForERNI: boolean | null;
    erNIPercent: number;
    vatERNIRate: number;
    vatERNIRateCode: Option<string> | null;
    invoiceForERNIDescription: string;
    invoiceERPension: boolean | null;
    vatERPensionRate: number;
    vatERPensionRateCode: Option<string> | null;
    invoiceERPensionDescription: string;
    invoiceAppLevy: boolean | null;
    vatAppLevyRate: number;
    vatAppLevyRateCode: Option<string> | null;
    invoiceAppLevyDescription: string;
    invoicePayrollFee: boolean | null;
    vatPayrollFeeRate: number;
    vatPayrollFeeRateCode: Option<string> | null;
    invoicePayrollFeeDescription: string;
    invoicingMethod: Option<string> | null;
    feePerEmployee: string;
    tieredFees: Array<TieredFee>;
    invoiceTemplate: Option<string> | null;
    invoiceMethod: Option<string> | null;
  };
  margin: {
    marginMethod: Option<string> | null;
    margin: string;
    minMargin: string;
    marginCapped: string;
    autoReduceMarginForNMW: boolean | null;
    marginTableEntries?: Array<{
      fromValue: string;
      toValue: string;
      marginAmount?: string;
      marginPercent?: string;
    }>;
  };
  holidayPay: {
    holidayPayMethod: Option<string> | null;
    umbrellaHolidayPayMethod: Option<string> | null;
    payeHolidayPayRate: string;
    umbrellaHolidayPayRate: string;
    accrueEmploymentCosts: boolean | null;
    includeInTimesheetRate: boolean | null;
    holidayPayCalculationRule: Option<string> | null;
  };
  tracking: {
    enableAWRTracking: boolean | null;
    enableWTR: boolean | null;
    notificationWeeks: string;
    takeHomePercent: string;
    salaryCalMethod: Option<string> | null;
    hoursCap: string;
    marginUmbrellaAdvanceAdjustment: boolean | null;
  };
  expense: {
    isRequiredAttachmentExpenses: boolean | null;
    reclaimAppLevy: boolean | null;
  };
  paymentDetails: {
    bankName: Option<string> | null;
    accountName: string;
    accountNumber: string;
    sortCode: string;
    buildingSocietyNumber: string;
    IBANReference: string;
    SWIFTNo: string;
  };
  restrictedHours: {
    isRestrictedHours: boolean | null;
  };
  communicationSettings: {
    isSendEmailSelfBill: boolean | null;
    isSendEmailP45: boolean | null;
    isSendEmailP60: boolean | null;
  };
  payslipSetting: {
    showHPEntitlement: boolean | null;
    showHPEmployerCostsYTD: boolean | null;
  };
  payslipTemplates: {
    logoUrl: File | string | null;
    bannerUrl: File | string | null;
    payePayslipTemplate: Option<string> | null;
    umbrellaPayslipTemplate: Option<string> | null;
    hybridPayslipTemplate: Option<string> | null;
    hybridPayePayslipTemplate: Option<string> | null;
    cisPayslipTemplate: Option<string> | null;
    payeETHPayslipTemplate: Option<string> | null;
  };
};

const defaultValues: PayrollFinanceFormValues = {
  invoicing: {
    vatRegNo: '',
    vatRate: null,
    domesticReverseChargeApplicable: null,
    creditLimit: '',
    creditTerm: '',
    paymentTerm: null,
    invoicingMethod: null,
    invoicingEmail: '',
    ccEmail: '',
  },
  invoiceSetting: {
    raiseInvoice: false,
    dueDate: '',
    rule: null,
    invoiceForERNI: false,
    erNIPercent: 0,
    invoiceForERNIDescription: '',
    invoiceERPension: false,
    invoiceERPensionDescription: '',
    invoiceAppLevy: false,
    invoiceAppLevyDescription: '',
    invoicePayrollFee: false,
    invoicePayrollFeeDescription: '',
    invoicingMethod: null,
    feePerEmployee: '',
    tieredFees: [],
    raiseInvoicePEO: false,
    invoiceRaisedAfter: null,
    invoiceRaisedMethod: null,
    vatERNIRate: 0,
    vatERNIRateCode: null,
    vatERPensionRate: 0,
    vatERPensionRateCode: null,
    vatAppLevyRate: 0,
    vatAppLevyRateCode: null,
    vatPayrollFeeRate: 0,
    vatPayrollFeeRateCode: null,
    vatSalaryRate: 0,
    vatSalaryRateCode: null,
    invoiceForSalary: false,
    invoiceForSalaryDescription: '',
    invoiceMethod: null,
    invoiceTemplate: null,
  },
  margin: {
    marginMethod: null,
    margin: '',
    minMargin: '',
    marginCapped: '',
    autoReduceMarginForNMW: null,
  },
  holidayPay: {
    holidayPayMethod: null,
    umbrellaHolidayPayMethod: null,
    payeHolidayPayRate: '',
    umbrellaHolidayPayRate: '',
    accrueEmploymentCosts: null,
    includeInTimesheetRate: null,
    holidayPayCalculationRule: null,
  },
  tracking: {
    enableAWRTracking: null,
    enableWTR: null,
    notificationWeeks: '',
    takeHomePercent: '',
    salaryCalMethod: null,
    hoursCap: '',
    marginUmbrellaAdvanceAdjustment: null,
  },
  expense: {
    isRequiredAttachmentExpenses: null,
    reclaimAppLevy: null,
  },
  paymentDetails: {
    bankName: null,
    accountName: '',
    accountNumber: '',
    sortCode: '',
    buildingSocietyNumber: '',
    IBANReference: '',
    SWIFTNo: '',
  },
  restrictedHours: {
    isRestrictedHours: null,
  },
  communicationSettings: {
    isSendEmailSelfBill: null,
    isSendEmailP45: null,
    isSendEmailP60: null,
  },
  payslipSetting: {
    showHPEntitlement: null,
    showHPEmployerCostsYTD: null,
  },
  payslipTemplates: {
    logoUrl: null,
    bannerUrl: null,
    payePayslipTemplate: null,
    umbrellaPayslipTemplate: null,
    hybridPayslipTemplate: null,
    hybridPayePayslipTemplate: null,
    cisPayslipTemplate: null,
    payeETHPayslipTemplate: null,
  },
};

export type PayrollFinanceFormRef = {
  submit: () => void;
};

type PayrollFinanceFormMode = 'create' | 'update';

const SECTION_VISIBILITY: Record<
  PayrollFinanceFormMode,
  {
    invoicingBasic: boolean;
    invoiceAdvanced: boolean;
    margin: boolean;
    holidayPay: boolean;
    paymentDetails: boolean;
    restrictedHours: boolean;
    tracking: boolean;
    communication: boolean;
    expense: boolean;
    payslipSetting: boolean;
    paymentModel: boolean;
    payslipTemplates: boolean;
  }
> = {
  create: {
    invoicingBasic: true,
    invoiceAdvanced: false,
    margin: true,
    holidayPay: true,
    paymentDetails: true,
    restrictedHours: false,
    tracking: false,
    communication: false,
    expense: false,
    payslipSetting: false,
    paymentModel: false,
    payslipTemplates: false,
  },
  update: {
    invoicingBasic: true,
    invoiceAdvanced: true,
    margin: true,
    holidayPay: true,
    paymentDetails: true,
    restrictedHours: true,
    tracking: true,
    communication: true,
    expense: true,
    payslipSetting: true,
    paymentModel: true,
    payslipTemplates: true,
  },
};

type PayrollFinanceFormProps = {
  mode?: PayrollFinanceFormMode;
  defaultValues?: Partial<{
    invoicing: Partial<PayrollFinanceFormValues['invoicing']>;
    invoiceSetting: Partial<PayrollFinanceFormValues['invoiceSetting']>;
    margin: Partial<PayrollFinanceFormValues['margin']>;
    holidayPay: Partial<PayrollFinanceFormValues['holidayPay']>;
    tracking: Partial<PayrollFinanceFormValues['tracking']>;
    expense: Partial<PayrollFinanceFormValues['expense']>;
    paymentDetails: Partial<PayrollFinanceFormValues['paymentDetails']>;
    restrictedHours: Partial<PayrollFinanceFormValues['restrictedHours']>;
    communicationSettings: Partial<
      PayrollFinanceFormValues['communicationSettings']
    >;
    payslipSetting: Partial<PayrollFinanceFormValues['payslipSetting']>;
    payslipTemplates: Partial<PayrollFinanceFormValues['payslipTemplates']>;
  }>;
  formRef?: Ref<PayrollFinanceFormRef>;
  onSubmit?: (values: PayrollFinanceFormValues) => void;
};

const PayrollFinanceForm: React.FC<PayrollFinanceFormProps> = ({
  formRef,
  onSubmit,
  defaultValues: defaultValuesProps,
  mode = 'update',
}) => {
  const formMode: PayrollFinanceFormMode = mode;
  useTypedIntl();
  const defaultPaymentModel = getAppConfig('paymentModels');
  const hasPayeFTH = some(
    defaultPaymentModel,
    (item) => item.value === PAYMENT_MODEL_ENUM.PAYE_FTH,
  );

  const [form] = Form.useForm<PayrollFinanceFormValues>();
  const watchValues = Form.useWatch([], form) ?? {};

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish: FormProps<PayrollFinanceFormValues>['onFinish'] = () => {
    const values = form.getFieldsValue(true);
    onSubmit?.(values);
  };

  const onFinishFailed: FormProps<PayrollFinanceFormValues>['onFinishFailed'] =
    (errorInfo) => {
      const fields = errorInfo?.errorFields
        ?.map((f) => f.errors?.[0])
        .filter(Boolean);
      if (fields?.length) {
        showMessage({ type: 'error', content: fields.join('. ') });
      }
    };

  return (
    <>
      <AppFormContainer
        form={form}
        initialValues={{
          ...defaultValues,
          ...defaultValuesProps,
        }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        scrollToFirstError
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <InvoicingBasicSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <MarginSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <PaymentDetailsSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <HolidayPaySection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <RestrictedHoursSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <TrackingSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <CommunicationSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <ExpenseSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        <PayslipSettingSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
        />
        {hasPayeFTH && SECTION_VISIBILITY[formMode].paymentModel && (
          <PaymentModelSection
            form={form}
            watchValues={watchValues as PayrollFinanceFormValues}
            formMode={formMode}
            visibility={SECTION_VISIBILITY[formMode]}
            hasPayeFTH={hasPayeFTH}
          />
        )}
        <PayslipTemplatesSection
          form={form}
          watchValues={watchValues as PayrollFinanceFormValues}
          formMode={formMode}
          visibility={SECTION_VISIBILITY[formMode]}
          hasPayeFTH={hasPayeFTH}
        />
      </AppFormContainer>
    </>
  );
};

export default PayrollFinanceForm;
